import { dispatch } from '../index'
import { API, graphqlOperation, Auth, Analytics } from 'aws-amplify'
import { includesAny } from '../../lib/arrayOperations'
import * as Sentry from '@sentry/browser'
import config from '../../configuration/config'
import history from '../../History'
import { StorageService } from '../../lib/StorageService'
import { getLatestAcknowledgements } from '../../components/Admin/Reports/Acknowledgement/Utils'

import {
    APP_TOGGLE_ADMIN_MODE,
    APP_SET_SEARCH,
    APP_UPDATE_ATTRIBUTE,
    HANDLE_CHANGE,
    SET_USER_ATTRIBUTES,
    SET_USER_DATA,
    SHOW_UPDATE_ATTRIBUTES,
    SET_TRAINING_RESULTS,
    UPDATE_WINDOW_DIMENSIONS,
    UPDATE_BUILDING_DATA,
    SHOW_BUILDING_PICKER,
    UPDATE_USER_DATA,
    SET_ADMIN_STATUS,
    APP_SHOW_ERROR_PAGE,
    APP_SET_PLAYLISTS,
    APP_SET_PROPERTY_PLAYLISTS,
    UPDATE_USER_ROLES,
    UPDATE_TRAINING_RESULT,
    SET_USER_OCCUPANCIES,
    APP_TEAM_MEMBERSHIP_DECLINED,
    APP_TEAM_MEMBERSHIP_ACCEPTED,
    APP_SET_TEAM_PROGRAMS,
    SHOW_TERMS_OF_USE,
    SET_TERMS_ACCEPTED
} from '../actionTypes'
import { teamProgramEntries } from '../../lib/teamPrograms'

const listOccupancy = `query($sk: String, $pk: ID) {
    listOccupancy(sk: $sk, pk: $pk) {
        items {
            pk
            sk
            d1
            d2
            roles
            firstName
            lastName
            email
            workPhone {
                number
                extension
            }
            phone {
                number
                extension
            }
            building
            property
            client
            tenant
            version
        }
    }
}`

// Positions with their assigned training programs. Read through getTeam, not
// listTeamPositions: the list resolver returns trainingPrograms empty (seen
// 2026-09-04 against a team whose editor showed programs on two positions),
// while the positions embedded on the team carry them — which is what the
// position editor and the assignment popup already rely on.
const getTeamPositionsOperation = `query GetTeamPositions($pk: ID) {
    getTeam(pk: $pk) {
        pk
        positions {
            sk
            title
            trainingPrograms {
                pk
                name
                userType
            }
        }
    }
}`

const operation = `query ($sk: String, $d2: ID) {
    getOccupancy(sk: $sk, d2: $d2) {
        ... on Occupant {
            pk
            sk
            d1
            d2
            haveLoggedIn
            firstName
            lastName
            roles
            primaryContact
            workPhone {
                number
                extension
            }
            phone {
                number
                extension
            }
            lastLogin
            metaData {
                createdAt
            }
            extraFields {
                key
                value
            }
            email
            version
            termsAcceptanceDate
            __typename
        }
        ... on EvacuationAssistance {
            pk
            sk
            d1
            assistanceNeeded
            email
            createdAt
            endDate
            floor
            firstName
            lastName
            phone {
                number
                extension
            }
            startDate
            suite
            version
            buddies
            __typename
        }
        ... on Building {
            pk
            d1
            name
            version
            settings {
                closedCaption
                requiredFloorWardens
            }
            createdAt
            address {
                city
                country
                name
                region
                state
                street
                zipCode
            }
            assemblyAreas {
                coordinates {
                  latitude
                  longitude
                }
                title
              }
            coordinates {
                latitude
                longitude
            }
            settings {
                dsal
                modules {
                    title
                    enabled
                    key
                }
                phoneNumbers {
                    name
                    number
                }
                dsal
                documentRoles
                systemConfig {
                    suites
                    support
                    systemNotifications
                    termsOfUse
                    userFields
                    whiteLabels
                    scheme
                    passwordLinkNeverExpiration
                    termsOfUse
                }
            }
            __typename
        }
        ... on Property {
            pk
            d1
            name
            createdAt
            version
            resources {
                local {
                    title
                    url
                }
                regional {
                    title
                    url
                }
                federal {
                    title
                    url
                }
            }
            settings {
                positions {
                    enabled
                    floorWarden
                    team
                }
                phoneNumbers {
                    name
                    number
                }
                multiBuilding
                suites
                modules {
                    title
                    enabled
                    key
                }
            }
            __typename
        }
        ... on Client {
            pk
            sk
            name
            logo
            color {
                r
                b
                g
            }
            __typename
        }
        ... on Tenant {
            pk
            sk
            d1
            name
            floors
            suites
            metaData { 
                createdAt
            }
            version
            __typename
        }
        ... on TrainingResult {
            certificateType
            completedAt
            createdAt
            d1
            d2
            expiredAt
            firstName
            lastName
            pk
            quizzesCompleted
            sk
            title {
                language
                text
            }
            updatedAt
            userType
            version
            videosCompleted
            __typename
        }
        ... on TeamMember {
            pk
            sk
            firstName
            lastName
            email
            tenant
            positionId
            floor
            suite
            assignedOn
            assignedBy
            acceptedOn
            __typename
        }
    }
}`

const listTrainingResults = `
    query ListTrainingResults(
        $sk: String!,
        $d2: String,
        $limit: Int,
        $nextToken: String
    ) {
        listTrainingResults(
            sk: $sk,
            d2: $d2,
            limit: $limit,
            nextToken: $nextToken
        ) {
            nextToken
            items {
                certificateType
                completedAt
                createdAt
                d1
                d2
                expiredAt
                firstName
                lastName
                pk
                quizzesCompleted
                sk
                title {
                    language
                    text
                }
                updatedAt
                userType
                version
                videosCompleted
            }
        }
    }
`

function isValidOccupancy(item) {
    if (!item || !item.sk) return false
    if (item.sk === 'OCCUPANCY#undefined' || item.sk === 'OCCUPANCY#null') return false
    const d1 = item.d1 || ''
    if (d1.startsWith('undefined') || d1 === '') return false
    return true
}

export const setSearch = (event) => dispatch({ value: event.target.value, type: APP_SET_SEARCH })
export const handleChange = (name) => (event) => dispatch({ name, event, type: HANDLE_CHANGE })
export const updateAttribute = (attr, val) => dispatch({ attr, val, type: APP_UPDATE_ATTRIBUTE })
export const updateBuildingData = (data) => dispatch({ data, type: UPDATE_BUILDING_DATA })

export const setTrainingResults = trainingResults => dispatch({ trainingResults, type: SET_TRAINING_RESULTS })
export const updateTrainingResult = newResult => dispatch({ newResult, type: UPDATE_TRAINING_RESULT })
export const updateWindowDimensions = (width, height) => dispatch({ width, height, type: UPDATE_WINDOW_DIMENSIONS })
export const showUserConfigurator = (show) => dispatch({ show: show, type: SHOW_UPDATE_ATTRIBUTES })
export const updateUser = (user) => dispatch({ data: user, type: UPDATE_USER_DATA })
export const setUserAttributes = (user) => dispatch({ user, data: user.attributes, type: SET_USER_ATTRIBUTES })
export const setPlaylists = (playlists) => dispatch({ playlists, type: APP_SET_PLAYLISTS })
export const setPropertyPlaylists = (playlists) => dispatch({ playlists, type: APP_SET_PROPERTY_PLAYLISTS })
export const updateUserRoles = (roles) => dispatch({ roles, type: UPDATE_USER_ROLES })
export const teamMembershipDeclined = ({ pk, sk, rolesRemoved = [] }) => dispatch({ pk, sk, rolesRemoved, type: APP_TEAM_MEMBERSHIP_DECLINED })
// programsAdded: lib/teamPrograms entries ({ pk, userType, teamPk }) for the
// accepted/joined position, so playlist eligibility can show exactly those.
export const teamMembershipAccepted = ({ pk, sk, acceptedOn, rolesAdded = [], programsAdded = [] }) =>
    dispatch({ pk, sk, acceptedOn, rolesAdded, programsAdded, type: APP_TEAM_MEMBERSHIP_ACCEPTED })
export const setTeamPrograms = (entries) => dispatch({ entries, type: APP_SET_TEAM_PROGRAMS })

export const showBuildingPicker = (show) => dispatch({ show, type: SHOW_BUILDING_PICKER })
export const showErrorPage = (show) => dispatch({ show, type: APP_SHOW_ERROR_PAGE })

export const setTermsAccepted = (accepted) => dispatch({
    type: SET_TERMS_ACCEPTED,
    payload: accepted
})

export const showTermsOfUse = (show) => dispatch({
    type: SHOW_TERMS_OF_USE,
    payload: show
})

export const getUserData = () => {
    Auth.currentAuthenticatedUser().then((user) => {
        dispatch({ user, data: user.attributes, type: SET_USER_ATTRIBUTES })

        Sentry.configureScope((scope) => {
            scope.setUser({ email: user.attributes.email })
        })

        // Check if this is a page refresh
        const isPageRefresh = !window.performance ||
            window.performance.navigation.type === window.performance.navigation.TYPE_RELOAD;

        // Check if we have a stored building selection in localStorage
        const savedBuilding = StorageService.get('currentBuilding');

        // If this is a page refresh and we have a saved building, try to use it directly
        if (isPageRefresh && savedBuilding) {
            // Try to reuse the saved building data on page refresh
            getProgramData(savedBuilding, user);
            // We'll still fetch occupancies but won't show the building picker
        }

        // Always fetch the latest occupancies
        API.graphql(graphqlOperation(listOccupancy, { pk: user.username, sk: 'OCCUPANCY' }))
            .then(res => {
                let occupancy = (res.data.listOccupancy.items || []).filter(isValidOccupancy);

                dispatch({
                    data: occupancy,
                    type: SET_USER_OCCUPANCIES
                });

                // If we already used a saved building from localStorage and we're on a page refresh,
                // don't show the building picker - we're already in the right building context
                if (isPageRefresh && savedBuilding) {
                    // No need to show the building picker or reprocess the building data
                    return;
                }

                // If we're not refreshing or don't have a saved building, 
                // handle the building selection logic
                if (occupancy.length > 1 && (!savedBuilding || !isPageRefresh)) {
                    // Show building picker if user has multiple buildings and no saved selection
                    // or if this is not a page refresh (e.g., initial load)
                    showBuildingPicker(true);
                } else if (occupancy.length > 1 && savedBuilding && isPageRefresh) {
                    const getBuildingTenantFromD1 = (d1) => {
                        const parts = (d1 || '').split('#')
                        return {
                            propertyId: parts[2],
                            buildingId: parts[3],
                            tenantId: parts[4]
                        }
                    }

                    const savedIds = getBuildingTenantFromD1(savedBuilding.d1)

                    // Try to find the matching occupancy for the saved building
                    const matchedOccupancy = occupancy.find(o =>
                        (o.pk === savedBuilding.pk && o.sk === savedBuilding.sk) ||
                        (o.d2 === savedBuilding.d2) ||
                        (() => {
                            const occIds = getBuildingTenantFromD1(o.d1)
                            return occIds.buildingId === savedIds.buildingId && occIds.tenantId === savedIds.tenantId
                        })()
                    );

                    if (matchedOccupancy) {
                        // Use the matched occupancy data
                        getProgramData(matchedOccupancy, user);
                    } else {
                        // If no match is found, show the building picker
                        showBuildingPicker(true);
                    }
                } else if (occupancy.length === 1) {
                    // User has only one building, use it
                    getProgramData(occupancy[0], user);
                } else if (occupancy.length === 0) {
                    // No occupancies found
                    console.error('No occupancies found for user');
                    showErrorPage(true);
                }
            })
            .catch(error => {
                const gqlErrors = error?.errors || []
                const partialItems = error?.data?.listOccupancy?.items || []

                console.error('Error fetching occupancies:', {
                    message: error?.message,
                    // Amplify rejects with the raw GraphQL response, so the
                    // real text lives in errors[].message — surface it.
                    errors: gqlErrors.map(e => `${(e.path || []).join('.') || '<root>'}: ${e.message || '<no message>'}`),
                    count: gqlErrors.length,
                    recovered: partialItems.length
                });

                const validItems = partialItems.filter(isValidOccupancy);
                if (validItems.length > 0) {
                    dispatch({
                        data: validItems,
                        type: SET_USER_OCCUPANCIES
                    });

                    if (validItems.length > 1) {
                        showBuildingPicker(true);
                    } else {
                        getProgramData(validItems[0], user);
                    }
                    return;
                }

                // Dispatch empty occupancies to prevent undefined errors
                dispatch({
                    data: [],
                    type: SET_USER_OCCUPANCIES
                });

                // Check if we're on password reset page - if so, don't show error
                const isResetPasswordPage = window.location.pathname.includes('reset-password');

                if (!isResetPasswordPage) {
                    // If occupancy fetch fails and we're not resetting password, show error page
                    // This can happen if DynamoDB records aren't ready yet after user recreation
                    console.warn('No occupancies found - user may need to log out and try again');
                    showErrorPage(true);
                }
            });
    });
}

export const getProgramData = async (data, user) => {
    // Always hide the building picker when we have data
    showBuildingPicker(false)

    // Also save the current building data in localStorage if not already saved
    // This ensures we have the data available on page refresh
    if (!data.__wasExplicitlySelected) {
        const savedData = {
            ...data,
            __wasExplicitlySelected: true
        };
        StorageService.set('currentBuilding', savedData);
    }

    // Amplify rejects with the raw GraphQL response ({ data, errors }) rather than an Error,
    // so `message` is undefined here and the real text only lives in errors[].message.
    // A partial failure still carries usable records, so recover them the way the
    // listOccupancy handler above and useGetOccupancy do instead of failing the whole login.
    const readOccupancyItems = (res) => (res?.data?.getOccupancy || []).filter(Boolean)
    const describeErrors = (gqlErrors) =>
        gqlErrors.map(e => `${(e.path || []).join('.') || '<root>'}: ${e.message || '<no message>'}`)

    let occupancyItems
    try {
        const response = await API.graphql(graphqlOperation(operation, {
            d2: data.d2
        }))
        occupancyItems = readOccupancyItems(response)
    } catch (errResponse) {
        const gqlErrors = errResponse?.errors || []
        occupancyItems = readOccupancyItems(errResponse)

        console.error('Error fetching getOccupancy:', {
            message: errResponse?.message,
            errors: describeErrors(gqlErrors),
            count: gqlErrors.length,
            recovered: occupancyItems.length
        })

        if (occupancyItems.length === 0) {
            Sentry.captureException(
                new Error(`getOccupancy failed for d2 ${data.d2}: ${describeErrors(gqlErrors).join('; ') || errResponse?.message || 'unknown error'}`)
            )
            showErrorPage(true)
            return null
        }

        console.warn(
            `getOccupancy returned partial GraphQL errors, continuing with ${occupancyItems.length} usable records`,
            gqlErrors
        )
    }

    let _property, _client, _occupant, _tenant, _evac, _building
    let _trainingResults = []
    let _teamMember = []
    let _siblingTeamMembers = []

    occupancyItems.forEach((d) => {
        switch (d.__typename) {
            case 'Property':
                _property = d
                break
            case 'Client':
                _client = d
                break
            case 'Occupant':
                _occupant = d
                break
            case 'Tenant':
                _tenant = d
                break
            case 'EvacuationAssistance':
                _evac = d
                break
            case 'Building':
                _building = d
                break
            case 'TrainingResult':
                _trainingResults.push(d)
                break
            case 'TeamMember':
                _teamMember.push(d)
                break
            default:
                break
        }
    })

    // Everything below dereferences _occupant (pk, d1). Without this guard a missing
    // Occupant record surfaces as a bare "cannot read property of undefined" further down,
    // which hides the real cause the same way the old catch block did.
    if (!_occupant) {
        console.error('getOccupancy returned no Occupant record:', {
            d2: data.d2,
            typenames: occupancyItems.map(d => d.__typename)
        })
        Sentry.captureException(new Error(`getOccupancy returned no Occupant record for d2 ${data.d2}`))
        showErrorPage(true)
        return null
    }

    // // Team positions assign training programs by playlist userType, but playlist
    // // visibility (dashboard card, training portal, training status) is driven by
    // // the Occupant record's roles. Derive those roles from the user's team
    // // memberships here so members see their programs even when the Occupant
    // // record's roles haven't been updated by the membership write yet.
    // try {
    //     const teamPks = [...new Set(_teamMember.map((m) => m.pk).filter(Boolean))]
    //     if (teamPks.length > 0) {
    //         const lastSegment = (value) => (value ? `${value}`.split('#').pop() : null)
    //         const myPositionIds = new Set(
    //             _teamMember.map((m) => lastSegment(m.positionId)).filter(Boolean)
    //         )

    //         const positionsArrays = await Promise.all(teamPks.map((pk) =>
    //             API.graphql(graphqlOperation(getTeamPositionsOperation, { pk }))
    //                 .then((res) => res.data.listTeamPositions.items || [])
    //                 .catch((err) => {
    //                     console.error('Error fetching team positions for team:', pk, err)
    //                     return []
    //                 })
    //         ))

    //         const derivedRoles = positionsArrays
    //             .flat()
    //             .filter((position) => myPositionIds.has(lastSegment(position.sk)))
    //             .flatMap((position) => (position.trainingPrograms || []).map((tp) => tp.userType))
    //             .filter(Boolean)

    //         if (derivedRoles.length > 0) {
    //             _occupant = {
    //                 ..._occupant,
    //                 roles: [...new Set([...(_occupant.roles || []), ...derivedRoles])],
    //             }
    //         }
    //     }
    // } catch (error) {
    //     console.error('Error deriving roles from team positions:', error)
    // }

    // Fetch training results from all occupancies in the same building
    // This ensures users with multiple tenants see consistent training status
    try {
        // Get all occupancies for this user
        const occupanciesResponse = await API.graphql(graphqlOperation(listOccupancy, {
            pk: _occupant.pk,
            sk: 'OCCUPANCY'
        }))

        const allOccupancies = occupanciesResponse.data.listOccupancy.items || []

        // Filter for occupancies in the same building
        const buildingId = _occupant.d1.split('#')[3]
        const sameBuildingOccupancies = allOccupancies.filter(occ => {
            const occBuildingId = occ.d1.split('#')[3]
            return occBuildingId === buildingId && occ.d2 !== data.d2
        })

        // Fetch training results for each occupancy in the same building
        if (sameBuildingOccupancies.length > 0) {
            const trainingPromises = sameBuildingOccupancies.map(occ =>
                API.graphql(graphqlOperation(operation, { d2: occ.d2 }))
                    .then(res => (res.data.getOccupancy || []).filter(Boolean))
                    .catch(err => {
                        console.error('Error fetching training results for d2:', occ.d2, err)
                        return []
                    })
            )

            const additionalItems = (await Promise.all(trainingPromises)).flat()
            _trainingResults.push(...additionalItems.filter(item => item.__typename === 'TrainingResult'))
            // Team memberships written against the user's OTHER occupant
            // records in this building count too — the programs a position
            // assigns follow the person, the same way training results do.
            _siblingTeamMembers.push(...additionalItems.filter(item => item.__typename === 'TeamMember'))
        }
    } catch (error) {
        console.error('Error fetching additional training results:', error)
        // Continue with just the current occupancy's results
    }

    // Programs assigned by the user's team positions (all memberships in the
    // building). Team playlists share one userType per group, so the roles
    // on the Occupant record can't say WHICH team programs apply — see
    // lib/playlistEligibility. Resolved before SET_USER_DATA so the training
    // surfaces never render a role-only pass first.
    let _teamPrograms = []
    try {
        const memberships = [..._teamMember, ..._siblingTeamMembers]
        const teamPks = [...new Set(memberships.map((m) => m.pk).filter(Boolean))]
        if (teamPks.length > 0) {
            const positionsByTeam = {}
            await Promise.all(teamPks.map((pk) =>
                API.graphql(graphqlOperation(getTeamPositionsOperation, { pk }))
                    .then((res) => { positionsByTeam[pk] = res.data.getTeam?.positions || [] })
                    .catch((err) => {
                        console.error('Error fetching team positions for team:', pk, err)
                        positionsByTeam[pk] = []
                    })
            ))
            _teamPrograms = teamProgramEntries(memberships, positionsByTeam)
        }
    } catch (error) {
        console.error('Error resolving team training programs:', error)
    }
    dispatch({ entries: _teamPrograms, type: APP_SET_TEAM_PROGRAMS })

    const floor = _occupant.d1.split('#')[5] || null
    const suite = _occupant.d1.split('#')[6] || null

    dispatch({
        data: {
            tenant: _tenant,
            building: _building,
            property: _property,
            client: _client,
            floor,
            suite,
            userId: user.username,
            teamMember: _teamMember,
            ..._occupant
        }, type: SET_USER_DATA
    })

    // Store all occupancies in Redux for use in sync operations
    try {
        const occupanciesResponse = await API.graphql(graphqlOperation(listOccupancy, {
            pk: _occupant.pk,
            sk: 'OCCUPANCY'
        }))
        const allOccupancies = occupanciesResponse.data.listOccupancy.items || []
        dispatch({ data: allOccupancies, type: SET_USER_OCCUPANCIES })
    } catch (error) {
        console.error('Error fetching occupancies:', error)
    }

    checkIfAdmin(_occupant)
    setTrainingResults(_trainingResults)

    // Check if this is a page refresh (no navigation history or direct URL access)
    const isPageRefresh = !window.performance ||
        window.performance.navigation.type === window.performance.navigation.TYPE_RELOAD;

    // Only redirect if this is NOT a page refresh OR we're switching buildings OR it's a tenant admin
    const isDirectURLAccess = !window.location.href.includes('?') && window.location.pathname === '/';
    const isTenantAdmin = _occupant?.roles?.includes('TENANT_ADMIN');
    const fromBuildingSelector = data.__fromBuildingSelector;
    const fromRouteSync = data.__fromRouteSync;

    // FIXED: Prevent dashboard flashing for registration link users with multiple associations
    // Don't redirect immediately if this data comes from registration link processing
    // Let the building picker handle the routing instead.
    // Also skip redirect when called from useSyncOccupancyFromRoute — the URL is already
    // correct (set by the browser back/forward action); we only need to update Redux state.
    const shouldRedirect = !fromRouteSync && (!isPageRefresh || fromBuildingSelector || isTenantAdmin);

    // Route users to appropriate pages
    if (shouldRedirect && (_occupant && _occupant.roles)) {
        const isPropertyAdmin = _occupant.roles.includes('PROPERTY_ADMIN')
        const isBuildingAdmin = _occupant.roles.includes('BUILDING_ADMIN')
        const isSuperAdmin = _occupant.roles.includes('SUPER_ADMIN')
        const isFSD = _occupant.roles.includes('FIRE_SAFETY_DIRECTOR')
        const isAdmin = isPropertyAdmin || isBuildingAdmin || isTenantAdmin || isSuperAdmin

        // Embed the occupancy d2 in history state for /dashboard and /trainingportal pushes.
        // The sync hook reads this state when the user navigates back to these URLs and
        // uses it to restore the correct building context in Redux.
        const occupancyState = { __occupancyD2: data.d2 }

        // Route according to the highest level role
        if (isSuperAdmin) {
            // Super admin should always go to dashboard
            history.push('/dashboard', occupancyState)
        } else if (isPropertyAdmin && _property) {
            history.push(`/admin/properties/${_property.pk}`)
        } else if (isBuildingAdmin && _building) {
            history.push(`/admin/buildings/${_building.pk}`)
        } else if (isTenantAdmin && _tenant) {
            // For tenant admin, check if they need to see the activation policy first
            // We need to check if tenant acknowledgments exist
            const checkTenantAcknowledgement = async () => {
                try {
                    const tenantAckQuery = `query GetTenant($pk: ID!, $sk: String) {
                        getTenant(pk: $pk, sk: $sk) {
                            ... on TenantAcknowledgements {
                                __typename
                                pk
                                sk
                                d1
                                building
                                tenant
                                acknowledgements {
                                    acknowledgedBy {
                                        pk
                                        firstName
                                        lastName
                                        email
                                    }
                                    active
                                    declined
                                    timestamp
                                }
                            }
                        }
                    }`

                    const response = await API.graphql(graphqlOperation(tenantAckQuery, {
                        pk: _tenant.pk,
                        sk: 'TENANT_ACKNOWLEDGEMENTS'
                    }))

                    let acknowledgementData = response.data.getTenant

                    // Handle case where response might be an array (shouldn't happen but being defensive)
                    if (Array.isArray(acknowledgementData)) {
                        acknowledgementData = acknowledgementData[0]
                    }

                    if (acknowledgementData) {
                        const latestAck = getLatestAcknowledgements(acknowledgementData)

                        // Determine if we should show the acknowledgment form
                        let shouldShowDashboard = false

                        if (!latestAck) {
                            // No acknowledgments exist, show dashboard (policy form will appear)
                            shouldShowDashboard = true
                        } else if (latestAck.declined) {
                            // Latest acknowledgment was declined, show dashboard (policy form will appear)
                            shouldShowDashboard = true
                        } else if (!latestAck.active) {
                            // Latest acknowledgment is not active, show dashboard (policy form will appear)
                            shouldShowDashboard = true
                        } else {
                            // Latest acknowledgment is active (accepted)

                            // If the latest acknowledgment was accepted by this user, go to admin dashboard
                            if (latestAck.acknowledgedBy?.pk === _occupant.pk) {
                                shouldShowDashboard = false
                            } else {
                                // Latest acknowledgment was accepted by someone else
                                // Check if this user has declined before
                                if (acknowledgementData && acknowledgementData.acknowledgements) {
                                    const userDeclinedAcks = acknowledgementData.acknowledgements.filter(ack =>
                                        ack.acknowledgedBy?.pk === _occupant.pk && ack.declined
                                    )

                                    if (userDeclinedAcks.length > 0) {
                                        // This user has declined before, check if someone else accepted after the decline
                                        const userLatestDecline = userDeclinedAcks.reduce((latest, current) =>
                                            (!latest.timestamp || current.timestamp > latest.timestamp) ? current : latest
                                        )

                                        // If the latest overall acknowledgment is accepted and happened after this user's decline
                                        if (latestAck.active && !latestAck.declined && latestAck.timestamp > userLatestDecline.timestamp) {
                                            // Another admin accepted after this user declined, go to admin dashboard
                                            shouldShowDashboard = false
                                        } else {
                                            // This user declined and no one else has accepted since, show dashboard
                                            shouldShowDashboard = true
                                        }
                                    } else {
                                        // This user hasn't declined, but someone else has accepted, go to admin dashboard
                                        shouldShowDashboard = false
                                    }
                                } else {
                                    // No acknowledgment data, but latestAck exists - should not happen, but be safe
                                    shouldShowDashboard = false
                                }
                            }
                        }

                        if (shouldShowDashboard) {
                            // User needs to see the tenant activation policy, route to dashboard
                            const isDashboardEnabled = _building?.settings?.modules?.some(
                                module => module.key === 'dashboard' && module.enabled
                            )
                            if (isDashboardEnabled) {
                                history.push('/dashboard', occupancyState)
                            } else {
                                history.push('/trainingportal', occupancyState)
                            }
                        } else {
                            // Policy is accepted, can go to admin dashboard
                            history.push(`/admin/tenants/${_tenant.pk}`)
                        }
                    } else {
                        // No acknowledgment data, route to dashboard to be safe
                        const isDashboardEnabled = _building?.settings?.modules?.some(
                            module => module.key === 'dashboard' && module.enabled
                        )
                        if (isDashboardEnabled) {
                            history.push('/dashboard', occupancyState)
                        } else {
                            history.push('/trainingportal', occupancyState)
                        }
                    }
                } catch (error) {
                    console.error('Error checking tenant acknowledgment:', error)
                    // On error, route to dashboard to be safe
                    const isDashboardEnabled = _building?.settings?.modules?.some(
                        module => module.key === 'dashboard' && module.enabled
                    )
                    if (isDashboardEnabled) {
                        history.push('/dashboard', occupancyState)
                    } else {
                        history.push('/trainingportal', occupancyState)
                    }
                }
            }

            checkTenantAcknowledgement()
        } else if (isFSD && _building) {
            // FSD without any other admin role gets the Administration module
            // (restricted Teams-only view) instead of the occupant dashboard.
            history.push(`/admin/buildings/${_building.pk}#teams`)
        } else if (!isAdmin) {
            // For non-admin users (occupants)
            // Check if dashboard module is enabled
            const isDashboardEnabled = _building?.settings?.modules?.some(
                module => module.key === 'dashboard' && module.enabled
            )

            if (isDashboardEnabled) {
                // If dashboard is enabled, direct to dashboard
                history.push('/dashboard', occupancyState)
            } else {
                // Otherwise, direct to training portal
                history.push('/trainingportal', occupancyState)
            }
        }
    } else if (isDirectURLAccess) {
        // If visiting root URL directly (not refresh), redirect to training portal
        history.push('/trainingportal')
    }
}

const checkIfAdmin = (user) => {
    let roles = user.roles
    const isAdmin = includesAny(roles, ['SUPER_ADMIN', 'PROPERTY_ADMIN', 'BUILDING_ADMIN', 'TENANT_ADMIN'])
    if (isAdmin) dispatch({ admin: true, type: SET_ADMIN_STATUS })
    return isAdmin
}
